import { type ProjectId, type ThreadId, type TurnId } from "@t3tools/contracts";
import { type ChatMessage, type SessionPhase, type Thread, type ThreadSession } from "../types";
import { type ComposerImageAttachment, type DraftThreadState } from "../composerDraftStore";
import { t } from "~/i18n";

/** In-memory thread view of a draft session; unlike a server thread it may not have a project yet. */
export type LocalDraftThread = Omit<Thread, "projectId"> & { projectId: ProjectId | null };

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
): LocalDraftThread {
  return {
    id: threadId,
    environmentId: draftThread.environmentId,
    codexThreadId: null,
    projectId: draftThread.projectId,
    title: t("common/newThread_iazwum"),
    session: null,
    messages: [],
    createdAt: draftThread.createdAt,
    archivedAt: null,
    latestTurn: null,
    activities: [],
  };
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  URL.revokeObjectURL(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") {
      continue;
    }
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

export function collectUserMessageBlobPreviewUrls(message: ChatMessage): string[] {
  if (message.role !== "user" || !message.attachments) {
    return [];
  }
  const previewUrls: string[] = [];
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") continue;
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrls.push(attachment.previewUrl);
  }
  return previewUrls;
}

export interface PullRequestDialogState {
  initialReference: string | null;
  key: number;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Could not read attachment data."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read attachment."));
    });
    reader.readAsDataURL(file);
  });
}

export function cloneComposerImageForRetry(
  image: ComposerImageAttachment,
): ComposerImageAttachment {
  if (typeof URL === "undefined" || !image.previewUrl.startsWith("blob:")) {
    return image;
  }
  try {
    return {
      ...image,
      previewUrl: URL.createObjectURL(image.file),
    };
  } catch {
    return image;
  }
}

export function deriveComposerSendState(options: { prompt: string; imageCount: number }): {
  trimmedPrompt: string;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = options.prompt.trim();
  return {
    trimmedPrompt,
    hasSendableContent: trimmedPrompt.length > 0 || options.imageCount > 0,
  };
}

export function threadHasStarted(thread: Thread | LocalDraftThread | null | undefined): boolean {
  return Boolean(
    thread && (thread.latestTurn !== null || thread.messages.length > 0 || thread.session !== null),
  );
}

export interface UploadProgressSnapshot {
  sentBytes: number;
  totalBytes: number;
  doneFiles: number;
  totalFiles: number;
}

export function formatUploadProgressLabel(progress: UploadProgressSnapshot): string {
  const percent =
    progress.totalBytes > 0
      ? Math.min(100, Math.floor((progress.sentBytes / progress.totalBytes) * 100))
      : 0;
  if (progress.totalFiles > 1) {
    const currentFile = Math.min(progress.doneFiles + 1, progress.totalFiles);
    const totalFiles = progress.totalFiles;
    return t("common/uploadingFiles_wy2kzs", { currentFile, totalFiles, percent });
  }
  return t("common/uploading_eztps3", { percent });
}

export interface LocalDispatchSnapshot {
  failureActivityIds: ReadonlySet<string>;
  startedAt: string;
  uploading: UploadProgressSnapshot | null;
  latestTurnTurnId: TurnId | null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionOrchestrationStatus: ThreadSession["orchestrationStatus"] | null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | LocalDraftThread | undefined,
  options?: { uploading?: UploadProgressSnapshot | null },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  return {
    failureActivityIds: new Set(
      activeThread?.activities.filter((a) => a.kind === "turn.failed").map((a) => a.id),
    ),
    startedAt: new Date().toISOString(),
    uploading: options?.uploading ?? null,
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionOrchestrationStatus: session?.orchestrationStatus ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  session: Thread["session"] | null;
  activities: Thread["activities"];
}): boolean {
  const localDispatch = input.localDispatch;
  if (!localDispatch) {
    return false;
  }
  if (
    input.activities.some(
      (a) => a.kind === "turn.failed" && !localDispatch.failureActivityIds.has(a.id),
    )
  ) {
    return true;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const latestTurnChanged =
    localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  if (input.phase === "running") {
    if (!latestTurnChanged) {
      return false;
    }
    if (latestTurn?.startedAt === null || latestTurn === null) {
      return false;
    }
    if (
      session?.activeTurnId !== undefined &&
      session.activeTurnId !== null &&
      latestTurn?.turnId !== session.activeTurnId
    ) {
      return false;
    }
    return true;
  }

  return (
    latestTurnChanged ||
    localDispatch.sessionOrchestrationStatus !== (session?.orchestrationStatus ?? null) ||
    localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
  );
}
